import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import MuiDivider from '@mui/material/Divider';
import { useLazyQuery } from '@apollo/client';
import { atom, useRecoilState, useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import type { Snapshot } from 'recoil';
import { useTrackFormFieldEdit } from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/hooks/useTrackFormFieldEdit';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { logger } from 'modules/logger';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import { Colors, Spacings } from 'styles';
import Text from 'common/ui/Text';
import { Stack } from 'common/ui/Layout';
import { ReactComponent as AutoAwesome } from 'assets/AutoAwesome.svg';
import Tooltip from 'common/ui/Tooltip';
import Info from '@mui/icons-material/Info';
import Replay from '@mui/icons-material/Replay';
import { FF, useSplitFlag } from 'modules/feature-flags/suspense';
import { HPConfirmationModal } from '../../../../HangingProtocol/Steps/HPConfirmationModal';
import { useClinicalStepDirtyState } from '../../../../HangingProtocol/hooks/useClinicalStepDirtyState';
import { DISCARD_CHANGES_MODAL } from '../../../../HangingProtocol/Steps/stepsModalFactory';
import {
  skipDiscardConfirmationState,
  initialHPFormStateSnapshot,
  hpFormStepsBaseline,
  steppedHPConfigurations,
  getStepsFromVersioned,
  hangingProtocolStepIndex,
} from '../../../../HangingProtocol/state';
import { useClinicalStepWriters } from '../../../../HangingProtocol/clinicalStepStore';
import { StepsConfigSection } from './StepsConfigSection';
import {
  DEFAULT_CREATE_HP_STEP,
  useFormStepOperations,
} from '../../../../HangingProtocol/hooks/useHPStepOperations';
import {
  useStepFormSync,
  stepLayoutsToLayouts,
  layoutsToStepLayouts,
} from '../../../../HangingProtocol/hooks/useStepFormSync';
import { useViewerLayoutWriters } from '../../../StudyLoader/viewerLayoutWriters';
import {
  fillMissingSeriesCriteria,
  preserveResolvedViewports,
} from '../../../../HangingProtocol/hpStepController';

import type { StepConfiguration } from '../../../../HangingProtocol/steppedHPTypes';
import { hasPersistedSteps, stepFromGqlFormat } from '../../../../HangingProtocol/steppedHPTypes';

import { Button } from 'common/ui/Button';
import { Field, FieldControl, FieldError } from '@ui/Field';
import type { HangingProtocol } from '../../../../HangingProtocol/useHangingProtocol';
import { broadcastHPInvalidation } from '../../../../HangingProtocol/useHangingProtocol';
import type { Layouts } from '../../../StudyLoader/viewerLoaderState';
import { layoutsForAllCasesState } from '../../../StudyLoader/viewerLoaderState';
import {
  activeStepViewportSeriesIdsSelector,
  flatBucketViewportSeriesIdsSelector,
  hangingProtocolLayoutsState,
} from '../../../../ViewportsConfigurations/state';
import type { ViewportSeriesIds } from '../utils/viewportsConfigToHangingProtocol';
import { generateViewportId, getLocalWindowId } from '../../../viewerUtils';
import { openedViewersState } from 'domains/viewer/TrackOpenedViewers';
import { StudyClassifierInput } from './StudyClassifierInput';
import type { GetHangingProtocolQuery, GetStudyClassificationQuery } from 'generated/graphql';
import { ViewType } from 'generated/graphql';
import type { SubmitFormIntent } from '../constants';
import { POWERED_BY_SIRONA_CLASSIFICATIONS, SUBMIT_FORM_INTENT, isPhase4Steps } from '../constants';

import CircularProgress from '@mui/material/CircularProgress';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { StudyClassificationDisplay } from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/HPv4/StudyClassificationDisplay';
import { useCreateHangingProtocol } from '../hooks/useCreateHangingProtocol';
import { useUpdateHangingProtocol } from '../hooks/useUpdateHangingProtocol';
import { useBaseViewerData } from '../../../StudyLoader/useStudies';
import { telemetryContextFor } from 'modules/Apollo/types';
import { GET_HANGING_PROTOCOL } from 'modules/Apollo/queries';
import { useGetMatchingStudyDescriptions } from './useGetMatchingStudyDescriptions';
import { MatchingDescriptionsTable } from './MatchingDescriptionsTable';
import { useNumberOfPriors } from '../utils/useNumberOfPriors';
import {
  areStepsUnchanged,
  hangingProtocolFormStateMergeFn,
  buildStepFormViewports,
  defaultHPFormState,
  formStateAfterSubmit,
  hpFormCloseState,
  flattenStudyClassification,
  generatePayload,
  hasUnsavedChanges,
  mergeStepViewportsForUpdate,
  unresolvedViewportIds,
  unresolvedStepViewports,
  describeViewportPosition,
  describeStepViewportPosition,
  NO_STEP_LAYOUT_CONTEXT,
  StepLayoutContextKind,
  withFormIntent,
  flattenSeriesClassification,
} from './hangingProtocolFormHelpers';
import type {
  HangingProtocolFormState,
  StepLayoutContext,
  StepViewportRef,
} from './hangingProtocolFormHelpers';
import {
  isUniqueNameError,
  getHangingProtocolErrorMessage,
} from '../utils/getHangingProtocolErrorMessage';
import { BORDER_COLOR, TABLE_BG_COLOR } from './hpStyles';
import { HP_ERROR_BG, HP_ERROR_BORDER } from '../../../../HangingProtocol/Steps/Common';
import { rootTheme } from 'theme/rootTheme';
import { ScrollContainer, SCROLL_VIEWPORT_ATTR } from 'common/ui/ScrollContainer/ScrollContainer';
import { ScrollOrientation } from 'common/ui/ScrollContainer/scrollMetrics';
export type {
  ExtendedStudyCriteria,
  ExtendedSeriesCriteria,
  HangingProtocolFormState,
} from './hangingProtocolFormHelpers';
export {
  hangingProtocolFormStateMergeFn,
  defaultHPFormState,
  flattenStudyClassification,
  flattenSeriesClassification,
  generatePayload,
  hasUnsavedChanges,
  mergeStepViewportsForUpdate,
  NO_STEP_LAYOUT_CONTEXT,
  StepLayoutContextKind,
} from './hangingProtocolFormHelpers';

/**
 * Bumped when the reader asks the form to retry classifications that failed.
 *
 * Lives here beside the other form atoms, and is read by each viewport rather than by the classifier
 * input itself, which keeps that input a pure leaf. Every viewport in every window has to hear it,
 * and the form that owns the retry button does not render those inputs directly.
 */
/** How long the Try again button stays disabled after a click, so one click is one wave. */
const CLASSIFICATION_RETRY_COOLDOWN_MS = 1500;

export const classificationRetryTokenState = atom<number>({
  key: 'classificationRetryToken',
  default: 0,
  // Broadcast, because a viewport that failed in the other viewer window is exactly the case the
  // submit gate exists to catch, and the retry button lives in whichever window holds the form. A
  // window-local token meant clicking it did nothing for that viewport and the reader had no way to
  // recover without walking to the other monitor.
  effects: [broadcastChannelSynchronizerEffect()],
});

export const hangingProtocolFormState = atom<HangingProtocolFormState>({
  key: 'hangingProtocolFormState',
  default: defaultHPFormState,
  effects: [broadcastChannelSynchronizerEffect({ mergeFn: hangingProtocolFormStateMergeFn })],
});

const formCss = css`
  background-color: ${Colors.gray3};
  margin: 0;
  max-height: 95vh;
  overflow: hidden;
`;

const headerCss = css`
  padding: ${Spacings.small}rem 16px 0;
  flex-shrink: 0;
`;

const bodyCss = css`
  padding: 16px 16px 0;
`;

const SectionDivider = styled(MuiDivider)`
  border-color: ${BORDER_COLOR};
  margin: 16px 0;
`;

/** Cap shared by both matching sections, so neither can push the sections below it off the form. */
const MATCHING_SECTION_MAX_HEIGHT_PX = 300;

/**
 * The matching-descriptions list scrolls within a capped height. Uses the custom indicator rather
 * than the native bar so the panel is the same width on Windows, where a native bar would otherwise
 * consume layout space and reflow the rows.
 */
const MatchingDescriptionsScroll = styled(ScrollContainer)`
  flex: 1;
  min-height: 0;
  max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;
  background-color: ${TABLE_BG_COLOR};
  border-radius: ${rootTheme.radius.xs}px;
  border: 1px solid ${BORDER_COLOR};

  /* The viewport is the element that scrolls, so the cap has to land on it. Its own height: 100% is
     indefinite here -- every ancestor up to the form body is auto-height -- which resolves to auto, so
     capping only an ancestor leaves the viewport as tall as its rows and it spills over the sections
     below instead of scrolling. */
  & > [${SCROLL_VIEWPORT_ATTR}] {
    max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;
  }
`;

/**
 * The criteria rows scroll on the same terms as the descriptions above. No background or border here: unlike
 * `MatchingDescriptionsTable`, `StudyClassificationDisplay` draws its own bordered box, and it is shared with the
 * viewport tile.
 */
const MatchingCriteriaScroll = styled(ScrollContainer)`
  flex: 1;
  min-height: 0;
  max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;

  & > [${SCROLL_VIEWPORT_ATTR}] {
    max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;
  }
`;

/**
 * The form body scrolls within the sidebar's fixed height. Uses the custom indicator rather than
 * the native bar for the same reason as `MatchingDescriptionsScroll` above: a native bar on Windows
 * would consume layout space and reflow the fields, making the panel a different width than on
 * macOS.
 */
const FormBodyScroll = styled(ScrollContainer)`
  flex: 1;
  min-height: 0;
`;

/**
 * The rejected-name treatment, matching the step chips: the same tinted fill and border, and the name itself in the
 * error colour. The border already came from `Field`'s invalid state; the fill and the text colour are what make it
 * read as the same state the reader sees on a duplicate step name.
 */
const nameErrorCss = css`
  [data-slot='field-control'] {
    background-color: ${HP_ERROR_BG};
    border-color: ${HP_ERROR_BORDER};
  }

  input {
    color: ${Colors.error};
  }
`;

const iconCss = css`
  width: 16px;
  height: 16px;
  color: ${Colors.gray8};
  cursor: pointer;
`;

export type HangingProtocolsFormProps = {
  /**
   * Called after a successful submit.
   *
   * `createdHangingProtocolSmid` is the protocol this submit created, for the caller to pin as the
   * applied one (VX-4905). CREATE mode only; never set on UPDATE, where the applied protocol does not
   * change.
   */
  onSubmit?: (HangingProtocolName: string, createdHangingProtocolSmid?: string) => void;
  onClose?: () => void;
  onError?: (err: Error) => void;
  hangingProtocol: Nullable<HangingProtocol>;
  mode: SubmitFormIntent;
  layouts: Layouts;
  studyDescriptions?: string[];
  groupedStudies?: string[];
  className: string;
  css?: FlattenSimpleInterpolation;
  /** Storybook-only props for forcing visual states. Not used in production. */
  __storyProps?: HangingProtocolsFormStoryProps;
};

/**
 * Storybook-only props for forcing visual states. Not used in production.
 *
 * The name error is only reachable through a submit the server rejects as a duplicate, so a story cannot mock its way
 * to it the way the classification errors do. Mirrors the escape hatches on `StepBar` and `StepBarChip`.
 */
export interface HangingProtocolsFormStoryProps {
  /** Renders the name field in its rejected state, with this message beneath it. */
  nameError?: string;
}

/**
 * Guards `data.viewportsConfigurations` before reading it: a `JSON` scalar, so its shape is
 * unenforced and has to be checked rather than cast. It is a **map keyed by viewport id**, despite
 * viewport configurations reading like a list everywhere else in the schema; treating it as an array
 * yields "this protocol stores no viewports", which callers would otherwise read as "nothing saved".
 *
 * Shared by `readStoredMirrorGroupIds` and `readStoredFlatViewports`, the two readers of this same
 * raw map -- keeping the shape guard in one place means a future change to what counts as malformed
 * only has to be made once, and the two stay consistent with each other by construction.
 */
function storedViewportEntries(viewportsConfigurations: unknown): Array<[string, unknown]> {
  if (viewportsConfigurations == null || typeof viewportsConfigurations !== 'object') return [];
  if (Array.isArray(viewportsConfigurations)) return [];
  return Object.entries(viewportsConfigurations);
}

/** The mirror group each viewport stores, out of a flat protocol's `data.viewportsConfigurations`. */
export function readStoredMirrorGroupIds(
  viewportsConfigurations: unknown
): Record<string, number | null> {
  return storedViewportEntries(viewportsConfigurations).reduce<Record<string, number | null>>(
    (acc, [viewportId, stored]) => {
      const groupId =
        stored != null && typeof stored === 'object'
          ? (stored as { viewportMirrorGroupId?: unknown }).viewportMirrorGroupId
          : undefined;
      acc[viewportId] = typeof groupId === 'number' ? groupId : null;
      return acc;
    },
    {}
  );
}

/** What a stored flat-protocol viewport carries, per `data.viewportsConfigurations`. */
interface StoredFlatViewportConfiguration {
  seriesCriteriaSourceText?: unknown;
  comparisonIndex?: unknown;
  viewType?: unknown;
}

export interface StoredFlatViewport {
  seriesCriteriaSourceText: string;
  comparisonIndex: number | null;
  viewType: ViewType;
}

const VIEW_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(ViewType));

/**
 * A flat protocol's viewports as they were *stored*, out of `data.viewportsConfigurations` -- the
 * same raw JSON scalar `readStoredMirrorGroupIds` reads.
 *
 * The typed `hangingProtocol.viewportsConfigurations` field the edit form otherwise seeds from is a
 * resolved projection: `getHangingProtocol`'s `enrichHangingProtocol` (graphql/src/resolvers/
 * hangingProtocol.ts) merges the stored viewports with the backend `/hangthis` service's per-case
 * match result (`classifierBasedHangingProtocolDeserializer`, graphql/src/util/viewer/
 * hangingProtocol/index.ts) -- observed (not just traced) to omit a viewport whose saved criteria
 * has no matching series in the case open right now, rather than keeping it unmatched. Seeding the
 * edit form from that field therefore drops the saved `seriesCriteriaSourceText` for any such
 * viewport and the fallback below marks it `isClearViewport: true`, even though the protocol still
 * has it saved. This reads the stored map instead, which -- like `data.viewportsConfigurations`
 * generally -- keeps every viewport slot regardless of whether it matches the case currently open.
 */
export function readStoredFlatViewports(
  viewportsConfigurations: unknown
): Record<string, StoredFlatViewport> {
  return storedViewportEntries(viewportsConfigurations).reduce<Record<string, StoredFlatViewport>>(
    (acc, [viewportId, stored]) => {
      if (stored == null || typeof stored !== 'object') return acc;
      const config = stored as StoredFlatViewportConfiguration;
      const viewType = VIEW_TYPE_VALUES.has(config.viewType as string)
        ? (config.viewType as ViewType)
        : ViewType.TwoDDre;
      acc[viewportId] = {
        seriesCriteriaSourceText:
          typeof config.seriesCriteriaSourceText === 'string'
            ? config.seriesCriteriaSourceText
            : '',
        comparisonIndex: typeof config.comparisonIndex === 'number' ? config.comparisonIndex : null,
        viewType,
      };
      return acc;
    },
    {}
  );
}

export const HangingProtocolsForm = (props: HangingProtocolsFormProps): React.ReactElement => (
  <Suspense fallback={null}>
    <HangingProtocolsFormInner {...props} />
  </Suspense>
);

const HangingProtocolsFormInner = ({
  layouts,
  hangingProtocol,
  mode,
  onSubmit,
  onClose,
  onError,
  className,
  __storyProps,
  studyDescriptions,
  groupedStudies,
}: HangingProtocolsFormProps): React.ReactElement => {
  const numberOfPriors = useNumberOfPriors({ context: APOLLO_QUERY_CONTEXT });
  // network-only: form-edit mutations only refetch GET_ALL_HANGING_PROTOCOLS, not this
  // per-id query, so cache-first could show a stale HP after a save. Revisit once the
  // save/edit flows patch or invalidate this query's cache entry (VX-4517).
  const [fetchHangingProtocol] = useLazyQuery<GetHangingProtocolQuery>(GET_HANGING_PROTOCOL, {
    fetchPolicy: 'network-only',
  });
  const { currentCase, currentCaseId } = useCurrentCaseContext();
  const createHangingProtocol = useCreateHangingProtocol({ context: APOLLO_QUERY_CONTEXT });
  const updateHangingProtocol = useUpdateHangingProtocol({ context: APOLLO_QUERY_CONTEXT });
  const { invalidateHangingProtocolMatch, refetch, classificationBySeriesSmid } = useBaseViewerData(
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );

  const primaryStudy = currentCase?.studies[0];

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Staged clinical step edits -- a drag or a grid change made in the clinical view and not saved,
  // which is what lights a step's dirty indicator. Read here because the UPDATE short-circuit below
  // cannot see them any other way: see the comment at that branch.
  const { hasAnyDirtyStep } = useClinicalStepDirtyState();
  // Tracks the mode+smid the form last initialized for (undefined = not yet initialized).
  // Prefixed with mode so CREATE and UPDATE can never collide, even if a real smid happened
  // to equal the CREATE sentinel. A genuine HP (or mode) switch re-initializes; same-HP
  // reference changes (step rebuilds from useHangingProtocol) do not wipe in-progress edits.
  const lastInitializedFormKeyRef = useRef<Nullable<string>>(undefined);
  const currentInitKey =
    mode === SUBMIT_FORM_INTENT.UPDATE ? `update:${hangingProtocol?.smid ?? ''}` : 'create';
  const [initializedFormKey, setInitializedFormKey] = useState<string | null>(null);
  const [initializationError, setInitializationError] = useState(false);
  const isInitialized = initializedFormKey === currentInitKey && !initializationError;
  // Bumped by every initialization pass and by unmount, so an async init that is still in flight
  // can tell it has been superseded. Without it, a fetch left over from a form the user already
  // closed resolves later and writes its steps into hpFormStepConfigurations -- a global atom that
  // outlives the form -- clobbering the freshly reopened form's steps (VX-4749).
  const initGenerationRef = useRef(0);
  // The generation whose `finishInit` actually ran. Compared against `initGenerationRef` to tell
  // "the latest init pass is still in flight" from "it finished", which the recovery effect below
  // needs and the key ref alone cannot answer -- that ref is claimed synchronously, before the fetch.
  const settledInitGenerationRef = useRef(0);
  /**
   * The mirror groups the protocol under edit already stores, keyed by viewport id.
   *
   * Filled from the UPDATE-mode raw fetch, which is case-*less* and so reports the stored value; the
   * matched protocol carries the resolved projection, where it is always null. A save that cannot see
   * which series each viewport holds carries these through rather than recomputing groups from
   * criteria alone -- see the mirror-group branch of `generatePayload`.
   */
  const storedMirrorGroupIdsRef = useRef<Record<string, number | null> | null>(null);
  /**
   * The flat protocol's stored viewport configurations, keyed by viewport id.
   *
   * Filled from the same UPDATE-mode raw fetch as `storedMirrorGroupIdsRef`, for the same reason:
   * the matched `hangingProtocol.viewportsConfigurations` prop drops any viewport whose saved
   * criteria doesn't match a series in the case currently open, instead of keeping it unmatched. Used
   * by `finishInit` to seed the edit form's viewport fields from what the protocol actually has
   * saved, not from what happens to match this case. `null` for a stepped HP or CREATE mode, where
   * this isn't fetched.
   */
  const storedFlatViewportsRef = useRef<ReturnType<typeof readStoredFlatViewports> | null>(null);
  const [hasNameBeenManuallyEdited, setHasNameBeenManuallyEdited] = useState(false);
  const [submittedNameError, setNameError] = useState<string | null>(null);
  const nameError = __storyProps?.nameError ?? submittedNameError;
  const [hasDuplicateStepNameError, setHasDuplicateStepNameError] = useState(false);
  /**
   * Viewports whose classification was not settled when the reader pressed save.
   *
   * Set at submit time from the payload rather than tracked continuously, which is what makes it see
   * viewports configured in the other viewer window: the form-state atom is broadcast and its merge
   * keeps the other window's viewports. Storing an unsettled viewport is the VX-4977 bug, and saving
   * with none of its criteria is what leaves it matching nothing and taking whatever series is left.
   */
  const [unresolvedViewports, setUnresolvedViewports] = useState<string[]>([]);
  // Held viewports the form is not showing, which live in the protocol's other steps. Kept apart
  // from the list above because the two ask different things of the reader: the retry below can
  // reach a viewport the form renders, and cannot reach one in another step.
  const [heldStepViewports, setHeldStepViewports] = useState<StepViewportRef[]>([]);
  const bumpRetryToken = useSetRecoilState(classificationRetryTokenState);
  /**
   * Whether a retry is outstanding, so a second wave cannot be launched on top of the first.
   *
   * Each click fires one classifier request per failed viewport, and a retry is most likely during a
   * classifier outage, which is the worst moment to let a reader multiply requests by their click
   * rate. Cleared when the viewports that were blocking either settle or fail again.
   */
  const [isRetrying, setIsRetrying] = useState(false);
  /**
   * Whether this form has already had a save held for classification.
   *
   * A ref rather than state because the submit callback is a `useRecoilCallback` that does not
   * depend on the banner, so reading state there would read whatever it closed over. Turning a
   * refusal count into a refusal-then-saved ratio is what separates readers who waited a moment
   * from readers who gave up.
   */
  const wasHeldForClassificationRef = useRef(false);
  const [formState, setFormState] = useRecoilState(hangingProtocolFormState);
  // Re-enable the retry once anything actually moved. Every classifier answer, failure and keystroke
  // writes viewport state, so the first change after a click means the click had an effect and the
  // reader is no longer just repeating it into a degraded classifier.
  useEffect(() => {
    setIsRetrying(false);
  }, [formState.viewports]);
  // And re-enable it on a timer regardless, because "wait for something to move" is exactly what a
  // sustained outage never does: a second failure carrying the same inputs is recorded as the
  // signature already on the viewport, which writes no new state, so the effect above would never
  // fire and the button would stay dead for the rest of the session. A reader who cannot retry is
  // worse off than a classifier fielding one more request.
  useEffect(() => {
    if (!isRetrying) return undefined;
    const timer = setTimeout(() => setIsRetrying(false), CLASSIFICATION_RETRY_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [isRetrying]);
  const phase = useSplitFlag(FF.HANGING_PROTOCOL_PHASE);
  const isHPStepsEnabled = isPhase4Steps(phase);
  const { applyFormStepLayouts, applyProtocolLayouts, restoreUserLayoutOverride } =
    useViewerLayoutWriters();
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [skipDiscardConfirmation, setSkipDiscardConfirmation] = useRecoilState(
    skipDiscardConfirmationState
  );
  const [initialFormSnapshot, setInitialFormSnapshot] = useRecoilState(initialHPFormStateSnapshot);
  const [initialStepSnapshot, setInitialStepSnapshot] = useRecoilState(hpFormStepsBaseline);
  const initialLayoutsRef = useRef<Layouts | null>(null);
  /**
   * Whether the layout the form opened on was the reader's own pick, rather than the protocol's.
   *
   * Cancel has to put back the layer it found, not just the value. Clearing the override is right
   * when the layout came from the protocol, and wrong when the reader had chosen it themselves --
   * `useLayouts` honours no other layer while the form is open, so clearing it drops a 3x3 back to
   * the matched protocol's grid (VX-5085). See `restoreUserLayoutOverride` in viewerLayoutWriters
   * for why only a snapshot that recorded a genuine override may restore one.
   */
  const openedOnUserLayoutOverrideRef = useRef(false);
  /**
   * The step the reader was on when the form opened, so cancelling can put them back.
   *
   * Needed because the form moves the *shared* step index while it is open -- `addStep` moves to the
   * step it created, and switching step in the form moves there too -- but it does not own that index
   * (`ownsStepIndex: false`), so nothing resets it on close. Adding a step and then discarding left
   * the index one past the end of the restored list: no chip matched it, so no step read as active
   * and the viewer fell back to the flat protocol, which is VX-4868's blank viewport. Mirrors
   * `initialLayoutsRef` above, which exists for the same reason on the layout side.
   */
  const stepIndexOnOpenRef = useRef<Nullable<number>>(null);
  const { applySavedSteps, applyProtocolName, selectStep } = useClinicalStepWriters();
  /**
   * The live step index, mirrored into a ref so the init effect can capture it without taking it as a
   * dependency -- it changes on every step navigation, and re-running init would wipe edits in
   * progress (see the guard at the top of that effect).
   */
  const liveStepIndex = useRecoilValue(hangingProtocolStepIndex);
  const liveStepIndexRef = useRef(liveStepIndex);
  liveStepIndexRef.current = liveStepIndex;
  const liveStepConfigurationsVersioned = useRecoilValue(steppedHPConfigurations);
  const liveStepConfigurations = getStepsFromVersioned(liveStepConfigurationsVersioned);

  /**
   * The viewer's own viewport assignments for the step the form is editing, read at submit time to
   * decide mirror groups. Memoized here because a `selectorFamily` call allocates.
   */
  const activeStepSeriesIds = useMemo(
    () => activeStepViewportSeriesIdsSelector(currentCaseId ?? null),
    [currentCaseId]
  );
  const flatBucketSeriesIds = useMemo(
    () => flatBucketViewportSeriesIdsSelector(currentCaseId ?? null),
    [currentCaseId]
  );

  /**
   * What each viewport currently holds, for the payload's mirror grouping.
   *
   * Three sources, because no single one covers every viewport of every step:
   *
   * 1. **`formState.viewports[*].seriesSmid`** -- what the user dropped in this form session. The
   *    only source that reflects an edit, because a drop in HP-form mode writes form state and
   *    deliberately does *not* touch the viewer's viewport configuration (see
   *    `ViewportDragDropTarget.handleDropThumbnail`). Highest precedence for that reason.
   * 2. **`activeStepViewportSeriesIdsSelector`** -- what the viewer has hung for the active step,
   *    which answers for the viewports in this step that the user left alone.
   * 3. **`steppedHPConfigurations`** -- the server-resolved series for every step, which answers for
   *    the steps the user never opened.
   *
   * A step nothing can answer for is deliberately left out of `byStep`, so `stepToGqlInput` preserves
   * the mirror groups that step already has rather than clearing them from evidence it does not have.
   */
  const readViewportSeriesIds = useCallback(
    (
      snapshot: Snapshot,
      flushedSteps: Nullable<StepConfiguration[]>
    ): { flat: ViewportSeriesIds; byStep: Record<string, ViewportSeriesIds> } => {
      // Lowest precedence first: what the reader hung *before* any step existed, which is the only
      // record of window 1's viewports at create time -- the active bucket is the new step's and holds
      // nothing yet, and the form renders the primary window's grid alone so it has no form state for
      // them either. Without this they save with no criteria at all (VX-5125).
      const fromFlatBucket = snapshot.getLoadable(flatBucketSeriesIds).getValue();
      const hung = snapshot.getLoadable(activeStepSeriesIds).getValue();
      const formViewports = snapshot.getLoadable(hangingProtocolFormState).getValue().viewports;

      // `seriesSmid` is optional, so `in` rather than a nullish check: an entry that carries the key
      // holding `null` is the user having retargeted the viewport away from any series, and it has to
      // beat the hung value rather than fall through to it.
      const flat: Record<string, string | undefined> = { ...fromFlatBucket, ...hung };
      for (const [viewportId, viewport] of Object.entries(formViewports)) {
        if (!('seriesSmid' in viewport)) continue;
        flat[viewportId] = viewport.seriesSmid ?? undefined;
      }

      const byStep: Record<string, ViewportSeriesIds> = {};
      const resolvedSteps =
        getStepsFromVersioned(snapshot.getLoadable(steppedHPConfigurations).getValue()) ?? [];
      for (const step of resolvedSteps) {
        if (step.smid == null) continue;
        const ids: Record<string, string | undefined> = {};
        for (const [viewportId, viewport] of Object.entries(step.viewports ?? {})) {
          ids[viewportId] = viewport.series_smid ?? undefined;
        }
        byStep[step.smid] = ids;
      }

      // The active step's entry is overwritten rather than merged: the viewer selector is the newer
      // statement of the same fact, and merging would let a stale resolution for a viewport the user
      // has just cleared survive as a mirror-group member.
      const activeStepSmid =
        flushedSteps?.[snapshot.getLoadable(hangingProtocolStepIndex).getValue()]?.smid;
      if (activeStepSmid != null) byStep[activeStepSmid] = flat;

      // Any remaining step nothing has answered for takes the flat answer.
      //
      // A step with no entry here is treated by `stepToGqlInput` as "no evidence, preserve whatever
      // groups this step already has" -- which for a step being created is *none*. So on a create
      // where the reader added a second step, the index has moved to that step, only it got the
      // active-step answer, and **step 1 saved with no mirror groups at all**. Two viewports holding
      // one series then reach the backend as two independent viewports asking the same question, and
      // it de-duplicates them: the first gets the series, the second gets the next one available.
      // Measured -- the reader's "Scout" in `Viewport-0-0-1` came back as "LUNG" (VX-5125).
      //
      // Only ever fills a gap. A step the reader never opened already has its own entry from
      // `steppedHPConfigurations` above, so the only steps this reaches are ones with no resolved
      // record -- brand-new steps, which are clones of the active one, and for which the active
      // step's answer is exactly right.
      for (const step of flushedSteps ?? []) {
        if (step.smid != null && byStep[step.smid] == null) byStep[step.smid] = flat;
      }

      return { flat, byStep };
    },
    [activeStepSeriesIds, flatBucketSeriesIds]
  );

  /**
   * Reads the case's user layout override off a snapshot, so the init effect can record whether one
   * existed without subscribing to the atom -- it is BroadcastChannel-synced and the form itself
   * writes it, so a subscription would re-render the form on its own writes.
   */
  const readUserLayoutOverride = useRecoilCallback(
    ({ snapshot }) =>
      (): Nullable<Layouts> =>
        currentCaseId != null
          ? (snapshot.getLoadable(layoutsForAllCasesState).getValue()[currentCaseId] ?? null)
          : null,
    [currentCaseId]
  );

  /**
   * Restores the viewer layouts to the values captured when the form opened.
   *
   * Which writer that needs depends on where the opening layout came from. A protocol-derived one is
   * re-published and the per-case override cleared, so subsequent step navigation (which only
   * updates hangingProtocolLayoutsState) is not shadowed by a stale entry in layoutsForAllCasesState.
   * A layout the reader picked themselves lived in that override and nowhere else, so clearing it
   * throws the layout away instead of restoring it -- that one goes back to the layer it came from.
   */
  const restoreInitialLayouts = useCallback(() => {
    if (initialLayoutsRef.current == null) return;
    if (openedOnUserLayoutOverrideRef.current) {
      restoreUserLayoutOverride(initialLayoutsRef.current);
      return;
    }
    applyProtocolLayouts(initialLayoutsRef.current);
  }, [applyProtocolLayouts, restoreUserLayoutOverride]);

  // Shared step operations hook
  const stepOps = useFormStepOperations();
  const { initializeSteps, resetSteps } = stepOps;

  /** Set once a submit succeeds, so the unmount below knows the session ended in a save. */
  const hasSubmittedRef = useRef(false);

  // Reset form step state on unmount to avoid stale data on next open
  useEffect(() => {
    return () => {
      resetSteps();
      lastInitializedFormKeyRef.current = undefined;
      initGenerationRef.current += 1;
      // A successful submit ends the session without `closeAndReset`, so this is where its close is
      // broadcast. Every other window still holds the saved session's viewports otherwise, and the next
      // Create inherits them: the form's init speaks only for this window, the merge keeps the other
      // windows' entries, and viewer/1's seed skips any viewport that already has source text. So a
      // protocol created after another one saved the previous protocol's window-1 series (VX-5148).
      // Written on unmount rather than on the success path, which would repaint the still-mounted form
      // blank before it closed (VX-3683). See `formStateAfterSubmit` for what this window keeps.
      if (hasSubmittedRef.current) {
        setFormState((prev) => formStateAfterSubmit(prev, getLocalWindowId()));
      }
    };
  }, [resetSteps, setFormState]);

  // Step-aware form sync: saves/loads form state when switching steps in edit mode
  const { switchToStep, flushCurrentStepToConfigurations } = useStepFormSync({
    formStateAtom: hangingProtocolFormState,
    setFormState,
  });

  // addStep/deleteStep now flush form state internally via the form flush
  // interceptor registered by useStepFormSync, so no wrapper is needed here.

  const studyClassification = useMemo(() => {
    return formState?.studyClassification ?? [];
  }, [formState]);

  const {
    captureOriginalValue: captureOriginalStudyDescription,
    trackEdit: trackStudyDescriptionEdit,
  } = useTrackFormFieldEdit<string>({
    metricName: viewer.usr.hangingProtocolStudyDescriptionEdited,
    isEnabled: true,
    additionalData: { hanging_protocol_smid: hangingProtocol?.smid ?? null },
  });

  const { captureOriginalValue: captureOriginalName, trackEdit: trackNameEdit } =
    useTrackFormFieldEdit<string>({
      metricName: viewer.usr.hangingProtocolNameEdited,
      isEnabled: true,
      additionalData: { hanging_protocol_smid: hangingProtocol?.smid ?? null },
    });

  const { descriptions: matchingDescriptions, loading: matchingDescriptionsLoading } =
    useGetMatchingStudyDescriptions(formState.studyCriteriaSourceText);

  const handleStudyClassificationChange = useCallback(
    (classification: GetStudyClassificationQuery['studyClassification']) => {
      const flattenedClassification = classification?.classification
        ? flattenStudyClassification(classification.classification)
        : [];

      setFormState((prev) => ({
        ...prev,
        studyClassification: flattenedClassification,
        ontologyVersion: classification?.classifierMetadata?.ontologyVersion ?? '',
      }));
    },
    [setFormState]
  );

  const handleProtocolNameChange = useCallback(
    (name: string) => {
      setNameError(null);
      captureOriginalName(formState.hangingProtocolName);
      setHasNameBeenManuallyEdited(true);
      setFormState((prev) => ({
        ...prev,
        hangingProtocolName: name,
      }));
    },
    [setFormState, captureOriginalName, formState.hangingProtocolName]
  );

  const handleStudyCriteriaSourceTextChange = useCallback(
    (description: string) => {
      captureOriginalStudyDescription(formState.studyCriteriaSourceText);
      setFormState((prev) => ({
        ...prev,
        studyCriteriaSourceText: description,
      }));

      // QOL: If user is creating a new HP, update name when description changes

      if (mode === SUBMIT_FORM_INTENT.SAVE && !hasNameBeenManuallyEdited) {
        // The name is about to change, so a "name is not unique" error from an earlier submit no
        // longer describes it. `nameError` is only ever set for that one error (see the submit catch),
        // and it also disables Create -- so leaving it set here left the button disabled after the
        // user had already fixed the collision. While the name is auto-generated, editing the
        // description is the *only* way to fix it, which made the dialog a dead end (VX-4530).
        setNameError(null);
        setFormState((prev) => ({
          ...prev,
          hangingProtocolName: description,
        }));
      }
    },
    [
      mode,
      setFormState,
      hasNameBeenManuallyEdited,
      captureOriginalStudyDescription,
      formState.studyCriteriaSourceText,
    ]
  );

  const closeAndReset = useCallback(() => {
    setHasNameBeenManuallyEdited(false);
    restoreInitialLayouts();
    // Put the reader back on the step they were on when the form opened. Only the close-without-
    // persisting paths reach here -- cancel, discard, and an Update with nothing changed -- and all
    // three discard the form's navigation along with its edits. A successful submit does not call
    // this; it keeps the reader on the step it just wrote (VX-4868).
    if (stepIndexOnOpenRef.current != null) {
      selectStep(stepIndexOnOpenRef.current);
      stepIndexOnOpenRef.current = null;
    }
    stepOps.dismissPendingUndoToasts();
    setFormState(hpFormCloseState);
    setInitialFormSnapshot(null);
    setInitialStepSnapshot(null);
    onClose?.();
  }, [
    setFormState,
    restoreInitialLayouts,
    selectStep,
    stepOps,
    setInitialFormSnapshot,
    setInitialStepSnapshot,
    onClose,
  ]);

  /**
   * The HP-owned stored layouts (active step's, or a flat HP's own) plus the layouts a save would
   * write, read from the same source flushCurrentStepToConfigurations/generatePayload read so the
   * two cannot disagree.
   *
   * Answers `{ kind: 'none' }` on CREATE (no existing HP layout to diff against), while steps are
   * enabled but not yet resolved (see the guard below), and on an UPDATE whose HP has no stored
   * `data.layouts`. Otherwise an UPDATE resolves to `ActiveStep` or `FlatHp` so a layout-only edit
   * is not invisible to hasUnsavedChanges (VX-4771, VX-4938).
   *
   * Both dirty checks below go through here, so they cannot disagree about what a save would write.
   */
  const readStepLayoutContext = useCallback(
    (snapshot: Snapshot): StepLayoutContext => {
      const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
      const caseLayouts = currentCaseId != null ? allCaseLayouts[currentCaseId] : null;
      const currentLayouts =
        caseLayouts ?? snapshot.getLoadable(hangingProtocolLayoutsState).getValue();

      if (isHPStepsEnabled) {
        // Steps load asynchronously (stepOps.steps starts null and is populated by a later
        // effect). While unresolved, this HP's stepped-ness is unknown, so a UPDATE for a
        // genuinely stepped HP must not fall through to the FlatHp branch below: its
        // hangingProtocol.data.layouts is a stale fallback the backend ignores once steps exist
        // (see generatePayload's comment), and diffing against it would compare the wrong value.
        // Staying on `none` here is safe -- flushCurrentStepToConfigurations() runs after steps
        // resolve, well before a real submit reaches the server.
        //
        // The one exception is a *known*-flat HP: `hangingProtocol.steps` (the fetched HP's own
        // step list, distinct from stepOps.steps) already reports zero steps, so there is nothing
        // for stepOps.steps to resolve into for this HP and waiting for it would never happen.
        // That case skips straight past the stepOps.steps indexing below -- it must not read
        // stepOps.steps while it is still null.
        if (stepOps.steps == null) {
          const hpSteps = hangingProtocol?.steps;
          const hpIsKnownFlat = Array.isArray(hpSteps) && hpSteps.length === 0;
          if (!hpIsKnownFlat) return NO_STEP_LAYOUT_CONTEXT;
        } else {
          const activeStep =
            stepOps.steps[snapshot.getLoadable(hangingProtocolStepIndex).getValue()];
          if (activeStep != null) {
            return {
              kind: StepLayoutContextKind.ActiveStep,
              activeStepLayouts: activeStep.layouts,
              currentLayouts,
            };
          }
        }
      }

      if (mode === SUBMIT_FORM_INTENT.UPDATE && hangingProtocol?.data?.layouts != null) {
        const storedLayouts = Object.fromEntries(
          hangingProtocol.data.layouts.map(({ id, layout }) => [id, layout.join(',')])
        );
        return {
          kind: StepLayoutContextKind.FlatHp,
          storedLayouts,
          currentLayouts,
        };
      }

      return NO_STEP_LAYOUT_CONTEXT;
    },
    [isHPStepsEnabled, stepOps.steps, currentCaseId, mode, hangingProtocol]
  );

  // Cancel reads the same layout state as submit. Without it a user who changed only a step's grid
  // got no discard prompt, and closeAndReset -> restoreInitialLayouts threw the change away in
  // silence -- the VX-4771 failure on the Cancel path rather than the Update path.
  const handleCancelClick = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const changesDetected = hasUnsavedChanges({
          currentFormState: formState,
          initialFormState: initialFormSnapshot as HangingProtocolFormState | null,
          currentStepConfigurations: stepOps.steps,
          initialStepConfigurations: initialStepSnapshot,
          stepContext: readStepLayoutContext(snapshot),
        });

        if (!changesDetected || skipDiscardConfirmation) {
          closeAndReset();
        } else {
          setShowDiscardModal(true);
        }
      },
    [
      formState,
      initialFormSnapshot,
      stepOps,
      initialStepSnapshot,
      skipDiscardConfirmation,
      closeAndReset,
      readStepLayoutContext,
    ]
  );

  const handleDiscardChanges = useCallback(() => {
    setShowDiscardModal(false);
    closeAndReset();
  }, [closeAndReset]);

  const handleKeepEditing = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleSubmit = useRecoilCallback(
    ({ snapshot }) =>
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const currentFormState = snapshot.getLoadable(hangingProtocolFormState).getValue();

        // If nothing at all is pending (UPDATE mode only), close without firing a mutation.
        //
        // `hasUnsavedChanges` answers "did this modal session change anything", and it cannot answer
        // more than that: every input it takes is captured *after* the clinical edits were staged --
        // `initialFormSnapshot` when the modal opened, and the layout comparison from the step atom --
        // so both sides of every comparison already contain them. A staged-only edit therefore reads as
        // unchanged, and short-circuiting on that alone meant Update silently discarded the reader's
        // staged drag and grid change and left the dirty indicator lit (VX-4932: "Unsaved changes
        // indicator remains after updating Hanging Protocol without viewport changes").
        //
        // The clinical dirty state is the missing half, so it is consulted alongside. Pressing Update
        // is an explicit save, and it now applies what is staged whether or not the modal itself was
        // touched. The short-circuit survives for the case it was actually written for -- an Update on a
        // protocol with nothing pending anywhere, which should not spend a mutation.
        //
        // Nothing else in the save path needed changing: when a mutation does fire, staged step edits
        // are already persisted and the indicator already clears
        // (`stepped-hp-pending-edits-in-form`'s final phase covered that before this fix).
        if (
          mode === SUBMIT_FORM_INTENT.UPDATE &&
          !hasAnyDirtyStep &&
          !hasUnsavedChanges({
            currentFormState,
            initialFormState: initialFormSnapshot as HangingProtocolFormState | null,
            currentStepConfigurations: stepOps.steps,
            initialStepConfigurations: initialStepSnapshot,
            stepContext: readStepLayoutContext(snapshot),
          })
        ) {
          closeAndReset();
          return;
        }

        // Refuse to save a protocol holding a viewport whose classification never settled.
        //
        // This reads the state being submitted rather than asking "is a query in flight here", which
        // is what the VX-4813 gate did. Two consequences, and both are the point: it sees viewports
        // configured in the other viewer window, because the form-state atom is broadcast and its
        // merge preserves them, and it does not release when a query errors, because a failure is
        // recorded on the viewport rather than inferred from a flag that clears itself.
        //
        // Two checks, because a protocol has two shapes and form state only describes one of them.
        // A flat protocol lives entirely in form state, so the signature check below is the whole
        // story. A stepped one holds one step in the form at a time, and the signatures do not
        // survive the trip into a step, so the steps the reader is not looking at are checked in
        // their payload shape instead -- see `unresolvedStepViewports`.
        //
        // A flat protocol's stored criteria are deliberately not seeded into form state on open (see
        // the init path: display names are not persisted and the `seriesCriteriaKeys` fragment
        // carries 6 of 15 categories, so a partial read would report every form as dirty). They are
        // repopulated by the classifier a moment later, from the source text that IS seeded. So a
        // loaded flat viewport is unresolved for as long as that takes and no longer, exactly like
        // every other viewport, and checking it does not hold Update up.
        //
        // This is the production case. An Update clicked before those answers land sends `criteria:
        // []` for a viewport nobody touched, which is what the 138 affected rows are. Scoping the
        // check to descriptions edited in this session, which is what this did at first, skips
        // precisely that.
        // Layouts read the same way readStepLayoutContext does, so the bounds filter below agrees
        // with what the save will actually send.
        const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
        const activeLayouts =
          (currentCaseId != null ? allCaseLayouts[currentCaseId] : null) ??
          snapshot.getLoadable(hangingProtocolLayoutsState).getValue();
        // Windows the reader can act in. `layoutsForAllCasesState` is the user-override layer and
        // nothing prunes it when a window closes, so a viewport configured on a second monitor that
        // is now shut still reads as in-bounds. Holding the save on one would name a position with
        // no classifier input mounted behind it, so it could never settle: the banner would ask for
        // something the reader cannot do from here and the retry would have nothing to reach.
        //
        // This window is added because the extension's list can come back without it (VX-4764), and
        // it is also the floor when that list is empty. Checking every window in that case looks
        // like the careful choice and is the opposite: it holds the save on viewports belonging to
        // windows there is no evidence are open, which is the one failure with no way out.
        const openWindowIds = snapshot.getLoadable(openedViewersState).getValue();
        const visibleWindowIds = new Set([...openWindowIds, getLocalWindowId()]);

        // Put the protocol into the shape that is going to be sent, before the gate below reads it.
        //
        // The flush writes the current step's form state into `hpFormStepConfigurations`, and the
        // criteria fill gives every viewport holding a series something to be matched on. Both have
        // to have happened first, or the gate judges something other than what the save sends: it
        // would miss a step the flush is about to write, and refuse a viewport the fill was about to
        // complete.
        //
        // Neither is a commitment to saving. The flush writes back the step the reader is already
        // on, which is what a step switch does anyway, and the fill's result is a local value. An
        // abort below leaves the form as the reader left it.
        let flushedStepConfigurations = flushCurrentStepToConfigurations();

        // Which series each viewport holds, which is what decides mirror groups. Read once and
        // used by both the dirty check and the payload, so the two cannot disagree about whether
        // this save regroups anything (VX-5005 / VX-5006).
        const seriesIds = readViewportSeriesIds(snapshot, flushedStepConfigurations);

        // Give every viewport that holds a series a classification to be matched on, before
        // anything reads these steps.
        //
        // The form's own classifier only covers the viewports the form renders, which is the
        // primary window's grid -- so on a two-window protocol the window-1 viewports would save
        // with no criteria at all, and the backend tries classification first and falls back to
        // description or remaining-series fill, which is how a reader's "Scout" came back as "ABD &
        // PEL DELAY" (VX-5125). Derived from the series' own classification rather than its
        // description text, for the reason VX-4970 established: the series record carries the
        // categories that actually discriminate. Adds only -- see `fillMissingSeriesCriteria`.
        if (flushedStepConfigurations != null) {
          const enriched = fillMissingSeriesCriteria(flushedStepConfigurations, {
            seriesIdForViewport: (step, viewportId) =>
              (step.smid != null ? seriesIds.byStep[step.smid] : undefined)?.[viewportId] ??
              seriesIds.flat[viewportId],
            criteriaForSeries: (seriesSmid) =>
              flattenSeriesClassification(
                classificationBySeriesSmid?.get(seriesSmid)?.seriesCriteria
              ).map((criterion) => ({ category: criterion.category, value: criterion.value })),
          });
          if (enriched.filled > 0) {
            logger.info('[HP] derived series criteria for viewports the form does not classify', {
              filled: enriched.filled,
              steps: enriched.steps.length,
            });
          }
          flushedStepConfigurations = enriched.steps;
        }

        const unresolved = unresolvedViewportIds(currentFormState, activeLayouts, visibleWindowIds);
        const unresolvedSteps = isHPStepsEnabled
          ? unresolvedStepViewports(flushedStepConfigurations, {
              currentStepIndex: stepOps.currentStepIndex,
              visibleWindowIds,
            })
          : [];
        if (unresolved.length > 0 || unresolvedSteps.length > 0) {
          setUnresolvedViewports(unresolved);
          setHeldStepViewports(unresolvedSteps);
          wasHeldForClassificationRef.current = true;
          // How often readers actually reach this is the only way to tell a fix that works from a
          // gate that annoys people. Counts and shapes only: a series description is free text a
          // reader may have typed anything into, so it does not go to analytics.
          try {
            analytics.track(viewer.usr.hangingProtocolSaveHeldForClassification, {
              intent: mode,
              is_stepped: (stepOps.steps?.length ?? 0) > 0,
              held_viewport_count: unresolved.length,
              held_step_viewport_count: unresolvedSteps.length,
              viewport_count: Object.keys(currentFormState.viewports).length,
            });
          } catch (analyticsError) {
            logger.error('Failed to track a held hanging protocol save', { analyticsError });
          }
          return;
        }
        setUnresolvedViewports([]);
        setHeldStepViewports([]);
        if (wasHeldForClassificationRef.current) {
          wasHeldForClassificationRef.current = false;
          try {
            // Counts a save that got through after being held, including one the reader unblocked by
            // clearing the description rather than by waiting. Both are recoveries; splitting them
            // needs a field, and only matters once the ratio says something.
            analytics.track(viewer.usr.hangingProtocolSaveResolvedAfterHold, { intent: mode });
          } catch (analyticsError) {
            logger.error('Failed to track a recovered hanging protocol save', { analyticsError });
          }
        }

        trackNameEdit(currentFormState.hangingProtocolName);
        trackStudyDescriptionEdit(currentFormState.studyCriteriaSourceText);
        setIsSubmitting(true);

        // Smid of the HP created in this submit (CREATE only). Passed to onSubmit so the widget can pin
        // it as the applied protocol, rather than leaving that to the post-create match (VX-4905).
        let createdHangingProtocolSmid: string | undefined;

        try {
          // Dismiss undo toasts immediately so the user can't revert step
          // changes while the mutation is in flight.
          stepOps.dismissPendingUndoToasts();

          // A `steps` array replaces the HP's entire step collection backend-side, minting new step
          // smids even when the only thing that changed was the HP's name. Omit it when the steps
          // are untouched, so a name- or criteria-only edit leaves the step rows (and every piece of
          // client bookkeeping keyed off their smids) alone (VX-4766). CREATE always sends steps:
          // there is nothing to preserve, and the flag would produce a stepless HP.
          const areStepsUntouched =
            mode === SUBMIT_FORM_INTENT.UPDATE &&
            areStepsUnchanged(flushedStepConfigurations, initialStepSnapshot, seriesIds.byStep);

          // A flat HP reaches the form carrying the single synthetic step /hang-cases normalizes it
          // into, so untouched steps on an HP with no persisted step rows means the HP is simply
          // still flat. It has to keep sending its layouts: both a `steps` array and the preserve
          // flag make the GraphQL layer null them, and the backend rejects an HP left with neither
          // (VX-4889). See `hasPersistedSteps`.
          const isStillFlatHP = areStepsUntouched && !hasPersistedSteps(hangingProtocol?.steps);

          const preserveExistingSteps = areStepsUntouched && !isStillFlatHP;

          const payload = generatePayload({
            formState: currentFormState,
            layouts,
            numberOfPriors,
            steps: isHPStepsEnabled && !isStillFlatHP ? flushedStepConfigurations : null,
            studyDescriptions,
            groupedStudies,
            preserveExistingSteps,
            viewportSeriesIds: seriesIds.flat,
            stepViewportSeriesIds: seriesIds.byStep,
            storedMirrorGroupIds: storedMirrorGroupIdsRef.current,
          });

          let serverStepSmids: Array<string | null> | null = null;
          if (mode === SUBMIT_FORM_INTENT.UPDATE) {
            if (hangingProtocol == null || hangingProtocol.smid == null) {
              throw new Error('Cannot update: hanging protocol not found');
            }

            const updateResult = await updateHangingProtocol({
              variables: {
                id: hangingProtocol.smid,
                payload: payload,
              },
            });
            // The server assigns real smids to steps this save created; the flushed steps still
            // carry the `step-{uuid}` placeholders the form generated. Keeping those is not
            // cosmetic -- `revertAllStepsToBaseline` treats a client-generated smid as "never
            // saved" and drops the step, so a later Reset All silently deleted a step the user had
            // already saved. Observed as a step bar losing a chip, with the baseline holding
            // ["<uuid>", "step-<uuid>"] and only the first surviving the filter.
            serverStepSmids =
              updateResult?.data?.updateHangingProtocol?.steps?.map((step) => step.smid ?? null) ??
              null;

            try {
              analytics.track(viewer.usr.hangingProtocolUpdated, {
                hanging_protocol_name: payload.name,
                hanging_protocol_smid: hangingProtocol.smid,
                intent: 'update',
                hanging_protocol_payload: {
                  name: payload.name,
                  study_descriptions: payload.studyDescriptions,
                  number_of_priors: payload.numberOfPriors,
                  study_criteria: payload.studyCriteria,
                  layouts: payload.data.layouts,
                  viewports_configurations: payload.data.viewportsConfigurations,
                },
              });
            } catch (analyticsError) {
              logger.error('Failed to track hanging protocol update analytics', { analyticsError });
            }
          } else {
            const result = await createHangingProtocol({
              variables: {
                payload: payload,
              },
            });

            if (result.data?.createHangingProtocol) {
              createdHangingProtocolSmid = result.data.createHangingProtocol.smid ?? undefined;
              try {
                analytics.track(viewer.usr.hangingProtocolCreated, {
                  hanging_protocol_name: payload.name,
                  hanging_protocol_smid: result.data.createHangingProtocol.smid,
                  intent: 'create',
                  hanging_protocol_payload: {
                    name: payload.name,
                    study_descriptions: payload.studyDescriptions,
                    number_of_priors: payload.numberOfPriors,
                    study_criteria: payload.studyCriteria,
                    layouts: payload.data.layouts,
                    viewports_configurations: payload.data.viewportsConfigurations,
                  },
                });
              } catch (analyticsError) {
                logger.error('Failed to track hanging protocol create analytics', {
                  analyticsError,
                });
              }
            }
          }
          // Store dirty step data for steps not edited in the form into the
          // pending restore atom BEFORE refetch. The reinit effect in
          // useHangingProtocol reads and merges this data when it writes
          // Success - close the widget and reset state.
          if (isHPStepsEnabled && flushedStepConfigurations != null) {
            // The form works in the protocol's stored shape, which is criteria-only: study_smid,
            // series_smid and stack_smid are null, and the server-side match is what resolves them
            // against the studies actually open. Writing the flushed steps in raw would replace the
            // resolved assignments with nulls and blank every viewport until a refresh re-ran the
            // match, because the re-init guard in useHangingProtocol stops the refetch below from
            // repairing them (VX-4764). Carry the resolution forward from what the viewer already
            // has instead.
            // Read the resolved steps from a snapshot rather than from the setter's `prev`: a Recoil
            // updater has to stay pure and may run more than once, so it is the wrong place to
            // compute a value the baseline write below also needs.
            const resolvedSteps = getStepsFromVersioned(
              snapshot.getLoadable(steppedHPConfigurations).getValue()
            );
            const merged = preserveResolvedViewports(flushedStepConfigurations, resolvedSteps);
            // Adopt the identities the server just handed back, positionally: the payload sends
            // steps in order and the backend recreates them in that order, so index is the mapping.
            // Only applied when the counts agree -- if they do not, this save is not the authority
            // on which step is which and the next match will reconcile them.
            if (serverStepSmids != null && serverStepSmids.length === merged.steps.length) {
              merged.steps = merged.steps.map((step, i) => ({
                ...step,
                smid: serverStepSmids![i] ?? step.smid,
              }));
            }
            // Split warn/info deliberately so the actionable case is queryable on its own. An
            // unresolved viewport renders blank until the next server match: expected right after
            // the user retargets one, a regression of VX-4764 if it shows up on an untouched save.
            const resolutionLog = {
              hangingProtocolSmid: hangingProtocol?.smid,
              reattached: merged.reattached,
              unresolved: merged.unresolved,
              steps: merged.steps.length,
            };
            if (merged.unresolved > 0) {
              logger.warn(
                '[HP] steps saved with viewports that resolve to no series',
                resolutionLog
              );
            } else if (merged.reattached > 0) {
              logger.info('[HP] carried resolved viewports through save', resolutionLog);
            }
            // One operation, both atoms. The dirty indicator diffs the steps against the baseline,
            // so writing one and not the other left every step the user had just saved reading as
            // unsaved (VX-4764). Keeping the two in step also means a later reinit sees no local
            // edits to preserve for these steps, and can take the server's copy. The merged steps
            // are what gets baselined, not the flushed ones, or the re-attached smids read as dirt.
            applySavedSteps({
              steps: merged.steps,
              name: currentFormState.hangingProtocolName,
            });
          } else {
            applyProtocolName({ name: currentFormState.hangingProtocolName });
          }
          await invalidateHangingProtocolMatch();
          await refetch();

          broadcastHPInvalidation();

          // The created protocol travels to the caller, which pins it as the applied one. The
          // post-create match is deliberately not consulted here: it governs what a *case load* hangs,
          // not whether the protocol the reader just built from this study gets shown to them
          // (VX-4905). Its smid could not answer that question anyway -- "the match landed elsewhere"
          // reads identically for a criteria tie and for a protocol that does not match this study at
          // all.
          // onSubmit closes the form synchronously (see HangingProtocolWidget's
          // onHangingProtocolFormSubmit), so this component unmounts right after this call. Resetting
          // local form state here raced that unmount: the reset is a Recoil write, so it re-rendered
          // this still-mounted form with default values for one paint before the close took effect,
          // flashing the form back to blank before the modal closed (VX-3683).
          hasSubmittedRef.current = true;
          onSubmit?.(currentFormState.hangingProtocolName, createdHangingProtocolSmid);
        } catch (err) {
          if (isUniqueNameError(err)) {
            setNameError(getHangingProtocolErrorMessage(err, mode));
          }
          onError?.(err as Error);
          return;
        } finally {
          // Reset on both success and error paths; the form can stay mounted
          // after submit, so the success path must clear the spinner too.
          setIsSubmitting(false);
        }
      },
    [
      closeAndReset,
      currentCaseId,
      hasAnyDirtyStep,
      initialFormSnapshot,
      initialStepSnapshot,
      createHangingProtocol,
      flushCurrentStepToConfigurations,
      hangingProtocol,
      invalidateHangingProtocolMatch,
      isHPStepsEnabled,
      refetch,
      layouts,
      mode,
      numberOfPriors,
      onError,
      onSubmit,
      readStepLayoutContext,
      readViewportSeriesIds,
      classificationBySeriesSmid,
      trackNameEdit,
      trackStudyDescriptionEdit,
      updateHangingProtocol,
      applySavedSteps,
      applyProtocolName,
      stepOps,
      studyDescriptions,
      groupedStudies,
    ]
  );

  /**
   * Initialize form state. Priority order:
   * 1. Existing hanging protocol
   * 2. Primary study
   * 3. Default HP form state (empty/nullish values)
   *
   * Note that viewports get handled separately in ViewportClassifications
   */
  // Clinical edits (rename, add, delete) persist to the backend and update
  // steppedHPConfigurations via Recoil. The Apollo cache also receives updates
  // from mutation responses. A refetch on form open was previously attempted
  // here but caused layout resets (the invalidateHPInitializationCache +
  // refetch chain overwrites hangingProtocolLayoutsState asynchronously,
  // which conflicts with restoreInitialLayouts on form close).

  useEffect(() => {
    // Guard against re-initialization on same-HP reference changes (e.g. useHangingProtocol
    // rebuilds hangingProtocol per step index), which would otherwise wipe in-progress edits.
    // UPDATE keys on the real smid. CREATE has no real HP identity -- hangingProtocol is just
    // whatever incidentally auto-matches the case -- so it ignores smid entirely; otherwise an
    // unrelated change to that match (e.g. a delete elsewhere) could trip the guard mid-create
    // and reset studyClassification without the classifier query refiring. Prefixed with mode
    // so CREATE and UPDATE keys can never collide, regardless of what a real smid contains.
    if (lastInitializedFormKeyRef.current === currentInitKey) return;
    lastInitializedFormKeyRef.current = currentInitKey;
    const initGeneration = ++initGenerationRef.current;
    setInitializationError(false);
    const handleInitializationFailure = () => {
      if (initGenerationRef.current !== initGeneration) return;
      setInitializedFormKey(null);
      setInitializationError(true);
      logger.warn('[HP] could not initialize the hanging protocol form');
    };

    // Eagerly set the form intent so that HangingProtocolViewport init effects
    // (which check prev.submitFormIntent inside their functional updater) see
    // the correct mode and skip their CREATE-mode initialization. Without this,
    // viewports briefly populate from the viewer's matched series (correct) and
    // then get overwritten when the async fetchAndInit completes with step data.
    //
    // withFormIntent also sets hpSmid and clears closeSignal -- see its doc comment.
    // Without clearing closeSignal, a `prev` still carrying `closeSignal: true` from the last
    // close (hpFormCloseState, written by closeAndReset) would ride along into this broadcast,
    // and the merge fn (hangingProtocolFormStateMergeFn) treats any `closeSignal: true` as an
    // authoritative close, resetting every window (including this one) back to the default
    // "creating" state regardless of the `mode` just set here (VX-3589).
    setFormState((prev) => withFormIntent(prev, mode, hangingProtocol?.smid ?? null));

    // Capture the current layouts so we can restore them if the user cancels, along with whether
    // they were the reader's own pick -- see openedOnUserLayoutOverrideRef.
    initialLayoutsRef.current = layouts;
    openedOnUserLayoutOverrideRef.current = readUserLayoutOverride() != null;
    // Same, for the step the reader is on. Read through the ref so this effect does not depend on an
    // index that changes with every step navigation.
    stepIndexOnOpenRef.current = liveStepIndexRef.current;

    // Reset the manual edit flag when form initializes
    setHasNameBeenManuallyEdited(false);

    /**
     * Finishes form initialization once step configurations are resolved.
     * Extracted so both the sync (create) and async (update) paths share
     * the same viewport / form-state setup logic.
     */
    const finishInit = (initialSteps: StepConfiguration[] | null) => {
      /**
       * The step the form opens on: the one the reader was already on, not step 0 (VX-4810).
       *
       * Opening on 0 was three bugs in one. It moved the reader off their step; it seeded the form's
       * viewports and published the grid from step 0 while `hangingProtocolStepIndex` still pointed at
       * step N, so the chips showed N active over step 0's content; and `addStep` clones
       * `currentSteps[currentStepIndex]`, so Add Step duplicated step 0. Step 1 is usually left at the
       * 2x2 default, which is why the report reads as "Add Step defaults to 2x2 on every window" --
       * measured on a two-step protocol whose step 2 was 1x3: opening the form dropped window 0 to 1,1
       * and the clone came out 1,1.
       *
       * Clamped by `initializeSteps`, which is also where the index is written.
       */
      const openedOnStepIndex = Math.max(stepIndexOnOpenRef.current ?? 0, 0);
      const initialStepIndex =
        initialSteps != null && initialSteps.length > 0
          ? Math.min(openedOnStepIndex, initialSteps.length - 1)
          : 0;

      initializeSteps(initialSteps, { stepIndex: initialStepIndex });

      // NOTE: Do NOT write initialSteps to steppedHPConfigurations (viewer atom)
      // here. The raw-fetched step data may have different layouts than the
      // current viewer, which would trigger useHangingProtocol's memo to rebuild
      // and change the viewport grid immediately on form open. The viewer atom
      // is synced on step switch (via switchToStep) and on save (via submit handler).

      // Build viewport form state. When steps exist, read directly from step 0
      // instead of hangingProtocol.viewportsConfigurations, which is derived from
      // the Recoil stepIndex and may still reflect the previous step on this render
      // (initializeSteps moves the index to the opening step, but the memo won't recompute until the
      // next render).
      const viewportsState: HangingProtocolFormState['viewports'] = {};

      const studyCriteriaSourceText =
        (mode === SUBMIT_FORM_INTENT.UPDATE
          ? hangingProtocol?.studyCriteriaSourceText
          : primaryStudy?.description) ?? '';

      if (initialSteps != null && initialSteps.length > 0) {
        // CREATE speaks only for this window; every other window seeds its own viewports from what
        // it has hung. See buildStepFormViewports for why claiming them all blanks viewer/1.
        Object.assign(
          viewportsState,
          buildStepFormViewports({
            step: initialSteps[initialStepIndex],
            layouts,
            restrictToWindowId: mode === SUBMIT_FORM_INTENT.SAVE ? getLocalWindowId() : undefined,
            studyDescription: studyCriteriaSourceText,
          })
        );
      } else {
        if (hangingProtocol?.viewportsConfigurations != null) {
          hangingProtocol.viewportsConfigurations.forEach((config) => {
            viewportsState[config.id] = {
              ...viewportsState[config.id],
              seriesCriteriaSourceText: config.configuration.seriesCriteriaSourceText ?? '',
              comparisonIndex: config.configuration.comparisonIndex ?? null,
              viewType: config.configuration.viewType ?? ViewType.TwoDDre,
              isClearViewport: false,
              // seriesClassificationCriteria: Intentionally omitted. Display name is not persisted, and needs to be re-fetched from the server. This will happen after form state is set and debounce expires.
              //
              // It also cannot be seeded from `configuration.seriesCriteria`, which is why the VX-4460
              // dirty check skips flat protocols (see `hasUnsavedChanges`). The `seriesCriteriaKeys`
              // fragment selects 6 of `SeriesCriteria`'s 15 categories, and `view_orientation` without
              // its `ontology_id`, so a flat HP's stored criteria cannot be reconstructed here. Seeding
              // from a partial read would report every form as dirty the moment the classifier resolved
              // a category the fragment does not carry. Widening the fragment is the prerequisite.
            };
          });
        }

        // When editing a non-stepped HP, viewports without configurations were cleared.
        // Mark them as isClearViewport: true by checking all viewport positions in the layout.
        if (mode === SUBMIT_FORM_INTENT.UPDATE && hangingProtocol?.data?.layouts != null) {
          hangingProtocol.data.layouts.forEach(({ id: windowId, layout }) => {
            const [rows, cols] = layout;
            for (let row = 0; row < rows; row++) {
              for (let col = 0; col < cols; col++) {
                const viewportId = generateViewportId(windowId, row, col);
                if (viewportsState[viewportId] != null) continue;

                // The matched data above has nothing for this viewport -- either its saved criteria
                // has no matching series in the case currently open, or it's genuinely empty. The
                // stored (case-less) data still has every viewport this protocol has ever saved, so
                // check there before concluding it was cleared (see readStoredFlatViewports).
                const stored = storedFlatViewportsRef.current?.[viewportId];
                const base = {
                  seriesCriteriaSourceText: stored?.seriesCriteriaSourceText ?? '',
                  comparisonIndex: stored?.comparisonIndex ?? null,
                  viewType: stored?.viewType ?? ViewType.TwoDDre,
                };
                viewportsState[viewportId] =
                  stored != null
                    ? { ...base, isClearViewport: false }
                    : { ...base, seriesClassificationCriteria: [], isClearViewport: true };
              }
            }
          });
        }
      }

      const hangingProtocolName =
        (mode === SUBMIT_FORM_INTENT.UPDATE ? hangingProtocol?.name : primaryStudy?.description) ??
        '';

      const initialFormState: HangingProtocolFormState = {
        hangingProtocolName,
        studyCriteriaSourceText,
        studyClassification: [],
        ontologyVersion: '',
        submitFormIntent: mode,
        // Discriminator for the cross-window merge fn: the HP smid when editing,
        // null when creating. Lets the merge distinguish a same-HP broadcast race
        // from a case/HP switch.
        hpSmid: mode === SUBMIT_FORM_INTENT.UPDATE ? (hangingProtocol?.smid ?? null) : null,
        viewports: viewportsState,
      };

      setFormState((prev) => ({
        ...prev,
        ...initialFormState,
        // Viewport classifiers live in separate viewer trees, including peer windows. Replay
        // cached results after this write clears criteria even when their source text is unchanged.
        initializationVersion: Math.max(Date.now(), (prev.initializationVersion ?? 0) + 1),
        // See the eager-set write above: `prev` can still carry a stale `closeSignal: true`
        // from the last close, which the merge fn would otherwise treat as an authoritative
        // close of this freshly-initialized state (VX-3589).
        closeSignal: false,
      }));

      // Capture initial state snapshot for change detection
      // Note: studyClassification and ontologyVersion will be populated later by the classifier,
      // so we capture them as empty here. The snapshot will be updated once classification is complete.
      setInitialFormSnapshot(initialFormState);
      // Capture initial step configurations (use the same object we set to stepConfigurations)
      setInitialStepSnapshot(initialSteps);

      // Sync the viewer layout to match the opening step's layout so the grid reflects the correct
      // dimensions when editing begins. The opening step, not step 0 -- see `initialStepIndex` above.
      if (initialSteps != null && initialSteps.length > 0) {
        // The *form* writer, for the same reason `useStepFormSync` uses it on a step switch: while
        // the form is open the case override is the only layer `useLayouts` honours, so publishing
        // to the protocol layer and clearing the override writes where nothing reads and drops the
        // layout the reader arrived with (VX-4810, VX-5085).
        applyFormStepLayouts(stepLayoutsToLayouts(initialSteps[initialStepIndex].layouts));
      }
      // Only arm the study classifier and editable fields after the whole initialization succeeds.
      settledInitGenerationRef.current = initGeneration;
      setInitializedFormKey(currentInitKey);
    };

    if (mode === SUBMIT_FORM_INTENT.SAVE) {
      // Nothing is stored yet, so the save computes every group from scratch.
      storedMirrorGroupIdsRef.current = null;
      storedFlatViewportsRef.current = null;
      // Create mode: start with a single default step, seeded with the current viewer layouts
      try {
        const defaultStep: StepConfiguration = {
          ...DEFAULT_CREATE_HP_STEP,
          layouts: layoutsToStepLayouts(layouts),
        };
        finishInit([defaultStep]);
      } catch {
        handleInitializationFailure();
      }
    } else if (mode === SUBMIT_FORM_INTENT.UPDATE) {
      // Always fetch raw HP for complete, unfiltered viewport data.
      // liveStepConfigurations (from the matched query) has filtered viewport
      // entries — viewports whose criteria don't match the current case are
      // stripped. The raw fetch preserves all viewport configurations.
      //
      // After fetching, merge with liveStepConfigurations to pick up clinical
      // edits (adds, deletes, renames, reorders) that only exist in the live
      // Recoil atom and haven't been persisted to the backend yet.
      const fetchAndInit = async () => {
        let fetchedSteps: StepConfiguration[] | null = null;
        if (hangingProtocol?.smid) {
          try {
            const { data } = await fetchHangingProtocol({
              variables: { id: hangingProtocol.smid },
              context: {
                ...APOLLO_QUERY_CONTEXT,
                telemetry: {
                  ...APOLLO_QUERY_CONTEXT.telemetry,
                  fetchPolicy: 'network-only',
                },
              },
            });
            // Read from `data.viewportsConfigurations`, not from the typed `viewportsConfigurations`.
            // The typed field is a resolved projection and a case-less fetch returns no entry at all
            // for a flat protocol, so the groups are simply absent there -- measured, after a first
            // cut of this read them from it and silently preserved nothing.
            //
            // No generation guard here, deliberately: these two refs are private to this component
            // instance, not the shared `hangingProtocolFormState` atom, so a superseded pass writing
            // them is inert -- the guard before `finishInit` below is what actually matters, since
            // that is what would otherwise let a stale pass publish into shared state. An early
            // return here before that point would have skipped the writes below for a pass whose
            // `finishInit` never runs anyway, and (VX-3745) skipped them for a pass that turned out
            // to still be current when this line's generation was read but not when the write it
            // gated ran -- an automated fix from a security scanner (8c13bf1049) added this same
            // no-op-in-theory guard here without noticing it wasn't a no-op.
            storedMirrorGroupIdsRef.current = readStoredMirrorGroupIds(
              data?.hangingProtocol?.data?.viewportsConfigurations
            );
            // Same rationale as the mirror groups above: for a flat (non-stepped) protocol, this is
            // what the edit form seeds its viewport fields from, instead of the matched
            // `hangingProtocol.viewportsConfigurations` prop, which drops any viewport whose saved
            // criteria has no matching series in the case currently open.
            storedFlatViewportsRef.current = readStoredFlatViewports(
              data?.hangingProtocol?.data?.viewportsConfigurations
            );
            const rawSteps = (data?.hangingProtocol?.steps ?? []).map(stepFromGqlFormat);
            if (rawSteps.length > 0) fetchedSteps = rawSteps;
          } catch (err) {
            logger.warn('Failed to fetch raw HP for form init, using matched data', { err });
          }
        }
        // Fall back to the matched HP data if the direct fetch returned no steps
        if (fetchedSteps == null) {
          const existingSteps = (hangingProtocol?.steps ?? []).map(stepFromGqlFormat);
          fetchedSteps = existingSteps.length > 0 ? existingSteps : null;
        }

        // Merge: use raw-fetched viewport data for persisted steps, but
        // include clinical-only steps (adds) from liveStepConfigurations.
        let mergedSteps = fetchedSteps;
        if (
          liveStepConfigurations != null &&
          liveStepConfigurations.length > 0 &&
          fetchedSteps != null
        ) {
          // Build a map of fetched steps by smid for viewport data lookup
          const fetchedBySmid = new Map(
            fetchedSteps.filter((s) => s.smid != null).map((s) => [s.smid, s])
          );

          // For each live step: merge fetched viewport data (complete, unfiltered) with any
          // live viewport that isn't yet persisted (e.g. a clinical drag-and-drop edit) so
          // neither an unmatched criteria definition nor an unsaved edit is lost (VX-4614).
          // Preserve live step order, names, and count (reflects clinical edits).
          mergedSteps = liveStepConfigurations.map((liveStep) => {
            const fetched = liveStep.smid != null ? fetchedBySmid.get(liveStep.smid) : null;
            if (fetched != null) {
              return {
                ...liveStep,
                viewports: mergeStepViewportsForUpdate(liveStep.viewports, fetched.viewports),
                // Preserve live step's display_name (may have been renamed clinically)
              };
            }
            // Clinical-only step (not yet persisted) — use live data as-is
            return liveStep;
          });
        } else if (liveStepConfigurations != null && liveStepConfigurations.length > 0) {
          // No fetched data available — use live state
          mergedSteps = liveStepConfigurations;
        }

        // The form may have been closed, reopened, or switched to another HP while this fetch
        // was outstanding. A superseded pass must not write its steps into the shared atom.
        if (initGenerationRef.current !== initGeneration) return;

        finishInit(mergedSteps);
      };
      void fetchAndInit().catch(handleInitializationFailure);
    }
  }, [
    hangingProtocol,
    liveStepConfigurations,
    layouts,
    mode,
    primaryStudy?.description,
    setFormState,
    initializeSteps,
    setInitialFormSnapshot,
    setInitialStepSnapshot,
    applyFormStepLayouts,
    readUserLayoutOverride,
    fetchHangingProtocol,
    currentInitKey,
    initializationError,
  ]);

  // Recovery for an init pass that had nothing to seed from.
  //
  // `finishInit(null)` is a legitimate outcome on its own -- there was genuinely nothing to show --
  // but that pass has already claimed `lastInitializedFormKeyRef`, so the key guard turns away every
  // later pass for the same protocol and the Steps section stays empty for the rest of this open,
  // however much state arrives afterwards. The form then renders no chips while the protocol and the
  // clinical step bar both hold the steps.
  //
  // Reaching "nothing to seed from" needs both halves, which is why it surfaced intermittently
  // rather than always: `mergedSteps` defaults to the fetched steps and is only replaced when the
  // live atom is non-empty, so the pass has to fetch nothing *and* see an empty live atom. A raw
  // fetch that throws is caught and warned, leaving exactly that whenever the pass also runs before
  // the viewer's step atom is populated.
  //
  // Deliberately narrow. It fires only after the init pass has finished and still left the form with
  // no steps while the viewer has some, adopts exactly what the init merge would have chosen in that
  // situation, and issues no fetch -- so it cannot become a re-initialisation loop, and it cannot
  // overwrite in-progress edits, because a form with edits has steps.
  //
  // Waiting for the pass to *settle* is the whole point, and the key ref cannot report that: init
  // claims it synchronously and only then awaits the fetch, so a recovery gated on the key alone
  // fires about a millisecond into every open and seeds the form roughly two seconds before the
  // fetch it was supposed to be a fallback for. That is not just wasted work -- steps arriving that
  // much earlier move `StepsConfigSection`'s auto-expand earlier with them, which is enough to flip
  // the outcome of anything timed against the section opening. Compare the generations instead.
  useEffect(() => {
    if (mode !== SUBMIT_FORM_INTENT.UPDATE) return;
    // Init has not run, or the pass in flight has yet to finish; let it do the seeding.
    if (lastInitializedFormKeyRef.current == null) return;
    if (settledInitGenerationRef.current !== initGenerationRef.current) return;
    if (stepOps.steps != null && stepOps.steps.length > 0) return;
    if (liveStepConfigurations == null || liveStepConfigurations.length === 0) return;
    initializeSteps(liveStepConfigurations);
  }, [mode, stepOps.steps, liveStepConfigurations, initializeSteps]);

  return (
    <Stack vertical stretchY className={className} css={formCss}>
      <Stack vertical css={headerCss}>
        <Text variant="display2">Hanging Protocol</Text>
        <Text
          css={css`
            font-size: 13px;
            color: ${Colors.gray8};
            padding-top: 4px;
          `}
        >
          {POWERED_BY_SIRONA_CLASSIFICATIONS}
        </Text>
      </Stack>

      <SectionDivider />

      <form
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0; /* Important for flex child to be scrollable */
        `}
        onSubmit={handleSubmit}
        data-testid="save-hanging-protocol-form"
      >
        {initializationError && (
          <Stack vertical role="alert" css={bodyCss}>
            <Text>Unable to load this hanging protocol. Try again.</Text>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                lastInitializedFormKeyRef.current = null;
                setInitializationError(false);
              }}
            >
              Retry
            </Button>
          </Stack>
        )}
        <FormBodyScroll
          orientation={ScrollOrientation.Vertical}
          data-testid="hanging-protocol-form-scroll"
        >
          <Stack
            vertical
            css={css`
              gap: 16px;
              ${bodyCss}
            `}
          >
            <Stack vertical>
              <Stack
                vertical
                css={css`
                  gap: 16px;
                `}
              >
                <Field
                  invalid={nameError != null}
                  css={nameError != null ? nameErrorCss : undefined}
                >
                  <Text
                    css={css`
                      font-size: 16px;
                      line-height: 24px;
                      color: ${Colors.gray10};
                    `}
                  >
                    Name this protocol
                  </Text>
                  <FieldControl
                    iconRight1={
                      hasNameBeenManuallyEdited ? (
                        <Tooltip content="Revert to auto-generated name" placement="top">
                          <Replay css={iconCss} />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          content="Auto-generated from study description. You can edit this name."
                          placement="top"
                        >
                          <Info css={iconCss} />
                        </Tooltip>
                      )
                    }
                    onClickIconRight1={
                      hasNameBeenManuallyEdited
                        ? () => {
                            setNameError(null);
                            setFormState((prev) => ({
                              ...prev,
                              hangingProtocolName: formState.studyCriteriaSourceText,
                            }));
                            setHasNameBeenManuallyEdited(false);
                          }
                        : undefined
                    }
                    aria-label-icon-right-1={
                      hasNameBeenManuallyEdited ? 'Revert to auto-generated name' : undefined
                    }
                  >
                    <input
                      name="name"
                      required
                      data-testid="protocol-field"
                      disabled={!isInitialized}
                      onChange={(e) => handleProtocolNameChange(e.target.value)}
                      value={formState.hangingProtocolName}
                      placeholder="Name your hanging protocol"
                    />
                  </FieldControl>
                  {nameError && (
                    <FieldError>
                      {/* Same Text treatment as the classifier's error below, rather than FieldError's own: its
                          Tailwind text-xs resolves against this app's 10px root and lands at 7.5px, half the size of
                          the error the reader sees two fields down. Borrowing the component keeps the two in step
                          instead of pinning a second set of numbers here. */}
                      <Text variant="caption" color="error">
                        {nameError}
                      </Text>
                    </FieldError>
                  )}
                </Field>
                <StudyClassifierInput
                  enabled={isInitialized}
                  value={formState.studyCriteriaSourceText ?? ''}
                  onClassificationChange={handleStudyClassificationChange}
                  onDescriptionChange={handleStudyCriteriaSourceTextChange}
                />
              </Stack>
              <Stack
                vertical
                stretchY
                css={css`
                  padding-top: 16px;
                `}
              >
                <Stack
                  alignY="center"
                  css={css`
                    gap: 6px;
                    padding-bottom: 8px;
                  `}
                >
                  <AutoAwesome
                    css={css`
                      width: 16px;
                      height: 16px;
                      flex-shrink: 0;
                      path {
                        fill: ${Colors.gray8};
                      }
                    `}
                  />
                  <Text
                    css={css`
                      font-size: 14px;
                      color: ${Colors.light};
                    `}
                  >
                    Matching classification criteria
                  </Text>
                </Stack>
                <Stack vertical stretchY>
                  <MatchingCriteriaScroll
                    orientation={ScrollOrientation.Vertical}
                    data-testid="matching-criteria-scroll"
                  >
                    <StudyClassificationDisplay studyClassification={studyClassification} />
                  </MatchingCriteriaScroll>
                </Stack>
              </Stack>

              <Stack
                vertical
                stretchY
                css={css`
                  padding-top: 16px;
                `}
              >
                <Stack
                  alignY="center"
                  css={css`
                    gap: 6px;
                    padding-bottom: 8px;
                  `}
                >
                  <AutoAwesome
                    css={css`
                      width: 16px;
                      height: 16px;
                      flex-shrink: 0;
                      path {
                        fill: ${Colors.gray8};
                      }
                    `}
                  />
                  <Text
                    css={css`
                      font-size: 14px;
                      color: ${Colors.light};
                    `}
                  >
                    Matching study descriptions
                  </Text>
                </Stack>
                <Stack
                  vertical
                  stretchY
                  css={css`
                    padding-bottom: ${Spacings.small}rem;
                  `}
                >
                  {matchingDescriptionsLoading ? (
                    <Stack alignX="center" alignY="center" stretchY>
                      <CircularProgress size={24} />
                    </Stack>
                  ) : (
                    <MatchingDescriptionsScroll
                      orientation={ScrollOrientation.Vertical}
                      data-testid="matching-descriptions-scroll"
                    >
                      <MatchingDescriptionsTable descriptions={matchingDescriptions} />
                    </MatchingDescriptionsScroll>
                  )}
                </Stack>
              </Stack>

              {isHPStepsEnabled && (
                <StepsConfigSection
                  {...stepOps}
                  defaultExpanded={stepOps.steps != null && stepOps.steps.length > 1}
                  onStepClick={switchToStep}
                  isSubmitting={isSubmitting}
                  onDuplicateError={setHasDuplicateStepNameError}
                />
              )}
            </Stack>
          </Stack>
        </FormBodyScroll>
        <SectionDivider />
        <Stack
          alignY="end"
          alignX="end"
          css={css`
            padding: 0 ${Spacings.small}rem 16px;
            flex-shrink: 0;
          `}
        >
          {unresolvedViewports.length > 0 || heldStepViewports.length > 0 ? (
            <div
              data-testid="unresolved-classification-error"
              css={css`
                flex: 1;
                margin-right: ${Spacings.small}rem;
              `}
            >
              {heldStepViewports.length > 0 ? (
                <Text variant="body2" color="error" data-testid="unresolved-step-classification">
                  {/* A separate sentence because the action is different: Try again re-runs the
                      classifier over the viewports this form renders, and cannot reach one in a step
                      the form is not showing. Naming the step is the only way the reader knows where
                      to go. */}
                  {`Couldn't classify ${
                    heldStepViewports.length === 1
                      ? 'the series description in'
                      : 'series descriptions in'
                  } ${heldStepViewports.map(describeStepViewportPosition).join(', ')}. Open that step and try again, or clear the description to save without it.`}
                </Text>
              ) : null}
              {unresolvedViewports.length > 0 ? (
                <Text variant="body2" color="error">
                  {`Couldn't classify ${
                    unresolvedViewports.length === 1
                      ? 'the series description in'
                      : 'series descriptions in'
                  } ${unresolvedViewports.map(describeViewportPosition).join(', ')}. Try again, or clear the description to save without it.`}
                </Text>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                data-testid="retry-classification"
                // Nothing to retry when every held viewport is in a step the form is not showing:
                // the retry token only re-runs the classifiers this form has mounted.
                disabled={isRetrying || unresolvedViewports.length === 0}
                onClick={() => {
                  try {
                    // A reader pressing this repeatedly is the signal of a form they cannot get out
                    // of, and it is invisible otherwise.
                    analytics.track(viewer.usr.hangingProtocolClassificationRetryClicked, {
                      held_viewport_count: unresolvedViewports.length,
                    });
                  } catch (analyticsError) {
                    logger.error('Failed to track a classification retry', { analyticsError });
                  }
                  setIsRetrying(true);
                  bumpRetryToken((token) => token + 1);
                }}
                // Untagged, like the Cancel button below: Button's css prop does not accept the
                // tagged helper's return type.
                css={`
                  border: none;
                  padding-left: 0;
                `}
              >
                Try again
              </Button>
            </div>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancelClick}
            data-testid="cancel-hanging-protocol-form"
            css={`
              border: none;
              margin-right: ${Spacings.small}rem !important;
            `}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            data-testid="submit-hanging-protocol-form"
            disabled={
              isSubmitting ||
              !isInitialized ||
              nameError != null ||
              hasDuplicateStepNameError ||
              !formState.hangingProtocolName?.trim()
            }
          >
            {isSubmitting ? (
              <CircularProgress
                size={16}
                sx={{ color: Colors.gray8 }}
                data-testid="submit-hanging-protocol-spinner"
                css={css`
                  margin-right: ${Spacings.xsmall}rem;
                `}
              />
            ) : null}
            {isSubmitting
              ? 'Submitting...'
              : mode === SUBMIT_FORM_INTENT.UPDATE
                ? 'Update'
                : 'Create'}
          </Button>
        </Stack>
      </form>
      {showDiscardModal && (
        <HPConfirmationModal
          {...DISCARD_CHANGES_MODAL}
          dontShowAgain={skipDiscardConfirmation}
          onDontShowAgainChange={setSkipDiscardConfirmation}
          onPrimaryAction={handleDiscardChanges}
          onSecondaryAction={handleKeepEditing}
          onClose={handleKeepEditing}
        />
      )}
    </Stack>
  );
};

HangingProtocolsForm.displayName = 'HangingProtocolsFormV4';
HangingProtocolsFormInner.displayName = 'HangingProtocolsFormV4Inner';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HangingProtocolsForm);
